import { deleteRoute, getRoute, postRoute } from "./client"
import type { AuthAccount } from "./auth"

export interface AdminSummary {
  totalUsers: number
  newUsers: number
  registeredUsers: number
  activeUsers: number
  currentHumans: number
  focuses: number
  completedFocuses: number
  focusMinutes: number
  averageFocusMinutes: number
  focusCompletionRate: number
  restStarts: number
  restMinutes: number
  averageUseMinutes: number
  averageVisitDays: number
  returningRate: number
  purchases: number
  buyers: number
  messages: number
  chatAuthors: number
}

export interface AdminOverview {
  range: { from: string; to: string }
  summary: AdminSummary
  conversion: { registrationRate: number; activationRate: number; shopRate: number; inviteRewards: number }
  acquisition: {
    registrations: number
    attributedRegistrations: number
    referralRegistrations: number
    rows: Array<{ source: string; medium: string; campaign: string; content: string; term: string; registrations: number; activated: number; email: number; vk: number }>
  }
  daily: Array<{ day: string; users: number; focuses: number; focusMinutes: number; restMinutes: number }>
  rooms: Array<{ id: string; title: string; visits: number; completed: number; minutes: number }>
  items: Array<{ item_id: string; title: string; category: string; purchases: number; buyers: number }>
}

export interface AdminUser {
  id: string
  userName: string
  userCity: string
  gender: string
  characterKey: string
  hairKey: string
  clothesKey: string
  studyType: string | null
  studyValue: string
  coins: number
  createdAt: string
  updatedAt: string
  lastActive: string
  registered: boolean
  provider: string
  accountName: string
  accountHint: string
  accountEmail: string
  avatarUrl: string
  vkFirstName: string
  vkLastName: string
  vkProfileUrl: string
  vkEmail: string
  vkPhone: string
  vkDataUpdatedAt: string
  focusCount: number
  completedFocusCount: number
  focusMinutes: number
  restMinutes: number
  inventoryCount: number
  purchaseCount: number
  registrationSource: string
  registrationMedium: string
  registrationCampaign: string
  referredRegistration: boolean
  referralStatus: string
  referralInviterName: string
}

export interface AdminMessage {
  id: string
  roomId: string
  userName: string
  text: string
  at: string
  isAdmin: boolean
  deletedAt: string | null
  deletionReason: string
  deletedBy: string
}

export interface AdminSocialVideoSubmission {
  id: string
  userId: string
  userName: string
  userCity: string
  accountName: string
  platform: string
  url: string
  status: "pending" | "approved" | "rejected"
  rewardCoins: number
  createdAt: string
  reviewedAt: string | null
  reviewNote: string
  telegramNotifiedAt: string | null
}

export interface AdminFeedbackTicket {
  id: string
  number: string
  kind: "idea" | "bug" | "question"
  message: string
  status: "new" | "in_progress" | "resolved"
  pagePath: string
  roomId: string
  viewportWidth: number
  viewportHeight: number
  userAgent: string
  screenshotUrl: string
  screenshotSize: number
  adminReply: string
  repliedAt: string | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  sender: { sessionId: string; userName: string; userCity: string; registered: boolean; provider: string; accountName: string; accountEmail: string; avatarUrl: string }
}

export const adminMeRoute = getRoute<{ account: AuthAccount; role: "owner" | "moderator" }>("/api/admin/me")
export const adminOverviewRoute = getRoute<AdminOverview>("/api/admin/overview")
export const adminUsersRoute = getRoute<{ total: number; limit: number; offset: number; users: AdminUser[] }>("/api/admin/users")
export const adminMessagesRoute = getRoute<{ messages: AdminMessage[] }>("/api/admin/messages")
export const adminAuditRoute = getRoute<{ items: Array<{ id: string; action: string; target_type: string; target_id: string; actor: string; details: Record<string, unknown>; created_at: string }> }>("/api/admin/audit")
export const adminSocialVideoSubmissionsRoute = getRoute<{ submissions: AdminSocialVideoSubmission[] }>("/api/admin/social-video-submissions")
export const adminFeedbackRoute = getRoute<{ tickets: AdminFeedbackTicket[] }>("/api/admin/feedback")
export const adminFeedbackStatusRoute = (id: string) => postRoute<{ success: true; status: AdminFeedbackTicket["status"] }>(`/api/admin/feedback/${encodeURIComponent(id)}/status`)
export const adminFeedbackReplyRoute = (id: string) => postRoute<{ success: true; status: AdminFeedbackTicket["status"] }>(`/api/admin/feedback/${encodeURIComponent(id)}/reply`)
export const adminReviewSocialVideoRoute = (id: string, action: "approve" | "reject") => postRoute<{ success: true; status: "approved" | "rejected" }>(`/api/admin/social-video-submissions/${encodeURIComponent(id)}/${action}`)
export const adminReplyRoute = postRoute<{ success: true; id: string }>("/api/admin/messages")
export const adminDeleteMessageRoute = (id: string) => deleteRoute<{ success: true }>(`/api/admin/messages/${encodeURIComponent(id)}`)
